import React, { useContext, useEffect, useRef } from 'react';
// Context
import { CalendarContext } from './context/CalendarContext';
import { useTranslationContext } from './context/TranslationContext';
// Components/ui
import { Backdrop, Box, Fade, Modal, Typography, IconButton, Snackbar, Alert } from '@mui/material';
import {
  ActionButtons,
  DatePicker,
  TaskDatePicker,
  DescriptionTextField,
  LocationTextField,
  LinkContactsSelect,
  ReminderNotifications,
  TitleTextField,
  TaskCalendarSelect,
  AllDayCheckbox,
  ColorPicker,
  RecurrenceSelect,
  ConfirmDeleteDialog,
  DeleteRecurringEventDialog,
  EditRecurringEventDialog,
} from './components';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CreateTypeTabs from './components/CreateTypeTabs/CreateTypeTabs';

const EventDialog: React.FC = () => {
  const t = useTranslationContext();

  const {
    event,
    task,
    dialogMode,
    selectedEvent,
    handleDeleteEvent,
    handleStartEditingEvent,
    handleDuplicateItem,
    dialogType,
    setDialogType,
    selectedTask,
    taskCalendarId,
    setTaskCalendarId,
    eventCalendarId,
    setEventCalendarId,
    myCalendars,
    otherCalendars,
    isLoadingCalendars,
    handleDeleteTask,
    userManuallyChangedColor,
    setUserManuallyChangedColor,
    eventDialog: { open, handleClose },
    confirmDeleteDialog,
    handleConfirmDelete,
    deleteRecurringDialog,
    handleConfirmDeleteRecurring,
    editRecurringDialog,
    handleConfirmEditRecurring,
    hiddenTabs,
    savingSnackbar,
    handleCloseSnackbar,
  } = useContext(CalendarContext);

  // 🔒 CORTOCIRCUITO DE SEGURIDAD
  // Si los hooks no están inicializados, no renderizar el contenido
  // Esto previene errores de "Cannot read properties of undefined"
  if (!event || !task) {
    return (
      <Modal
        open={open}
        onClose={handleClose}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 200 } }}
      >
        <Fade in={open} timeout={200}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 'min(640px, calc(100vw - 32px))',
              height: 'min(560px, calc(100vh - 64px))',
              //@ts-ignore
              bgcolor: (theme) => theme.palette.background.panel,
              borderRadius: 3,
              boxShadow: 24,
              outline: 0,
              p: 3,
              overflow: 'visible',
            }}
          />
        </Fade>
      </Modal>
    );
  }

  const isEvent = dialogType === 'event';
  const form = isEvent ? event : task;
  const selectedItem = isEvent ? selectedEvent : selectedTask;

  // 🔒 Ref para evitar llamadas innecesarias a setError
  const lastErrorStateRef = useRef<boolean | null>(null);

  // Resetear refs cuando se cierra el modal
  useEffect(() => {
    if (!open) {
      lastErrorStateRef.current = null;
    }
  }, [open]);

  // Extraer campos comunes
  const { titleField, descriptionField, date, linkContact } = form;

  // Notificaciones solo están en eventos
  const notifications = isEvent ? event.notifications : null;

  // Campos específicos de eventos
  const eventFields = isEvent
    ? {
        locationField: event.locationField,
        colorField: event.colorField,
        allDay: event.allDay,
        setAllDay: event.setAllDay,
        isPrivate: event.isPrivate,
        setIsPrivate: event.setIsPrivate,
        recurrenceRule: event.recurrenceRule,
        setRecurrenceRule: event.setRecurrenceRule,
      }
    : null;

  const dialogTitle =
    dialogMode === 'edit'
      ? t(isEvent ? 'global.editEvent' : 'global.editTask')
      : dialogMode === 'create'
        ? t(isEvent ? 'global.createEvent' : 'global.createTask')
        : t(isEvent ? 'global.showEvent' : 'global.showTask');

  const canDelete = (dialogMode === 'edit' || dialogMode === 'view') && Boolean(selectedItem?.id);
  const isViewMode = dialogMode === 'view';

  // Sincronizar el color del evento con el calendario seleccionado
  useEffect(() => {
    // Solo en modo creación de evento
    if (dialogMode !== 'create' || dialogType !== 'event' || !open) return;

    // Solo si el usuario no ha cambiado el color manualmente
    if (userManuallyChangedColor) return;

    // Asegurarse de que eventCalendarId tenga un valor
    if (!eventCalendarId) return;

    // Buscar el calendario seleccionado y actualizar el color
    const selectedCalendar = myCalendars.find((cal) => cal.id === eventCalendarId);
    if (selectedCalendar?.color) {
      event.colorField.actions.changeValue(selectedCalendar.color);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dialogMode,
    dialogType,
    open,
    eventCalendarId,
    userManuallyChangedColor,
    myCalendars,
    // event.colorField.actions es estable (memoizado con useCallback)
  ]);
  useEffect(() => {
    if (!open) return;
    if (dialogType !== 'event') return;
    // SOLO validar en modo EDICIÓN, no en view ni create
    if (dialogMode !== 'edit') return;

    const hasError =
      date.state.selectedStartDate.isAfter(date.state.selectedEndDate) &&
      !date.state.selectedStartDate.isSame(date.state.selectedEndDate, 'minute');

    // 🔒 PROTECCIÓN CONTRA LOOPS: Solo llamar si el estado de error cambió
    if (lastErrorStateRef.current === hasError) return;
    lastErrorStateRef.current = hasError;

    date.action.setError(hasError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    dialogType,
    dialogMode,
    // Comparar por timestamp en lugar de referencias de objeto
    date.state.selectedStartDate.valueOf(),
    date.state.selectedEndDate.valueOf(),
    // date.action.setError es estable (memoizado)
  ]);

  // NOTA: La sincronización del contacto se hace en useEvent/useTask hooks
  // mediante el efecto de sincronización por selectedEvent?.id / selectedTask?.id.
  // No se duplica aquí para evitar conflictos al cambiar de view → edit.

  const handleChangeCreateType = (value: 'event' | 'task') => {
    if (value === 'task') {
      const start = event.date.state.selectedStartDate;

      // tarea: start=end
      task.date.action.setSelectedStartDate(start);
      task.date.action.setSelectedEndDate(start);
      task.date.action.setError(false);
      // También resetear error del evento
      event.date.action.setError(false);
    } else {
      const start = task.date.state.selectedStartDate;

      // evento: copiamos start, y NO tocamos end (para respetar si el user ya tenía rango)
      event.date.action.setSelectedStartDate(start);

      // si el evento estaba inválido por error previo, lo limpiamos acá
      // (y el useEffect del modal lo vuelve a validar igual)
      event.date.action.setError(false);
      // También resetear error de la tarea
      task.date.action.setError(false);
    }

    setDialogType(value);
  };

  // Handler para detectar que el usuario cambió el color manualmente
  const handleColorChange = (newColor: string) => {
    event.colorField.actions.changeValue(newColor);
    // Marcar que el usuario cambió el color manualmente
    setUserManuallyChangedColor(true);
  };

  const baseSx = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 'min(640px, calc(100vw - 32px))',
    maxHeight: 'calc(100vh - 64px)',
    //@ts-ignore
    bgcolor: (theme) => theme.palette.background.panel,
    borderRadius: 3,
    boxShadow: 24,
    outline: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 200 } }}
      >
        <Fade in={open} timeout={200}>
          <Box sx={baseSx}>
            {/* Contenido scrolleable */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                p: 3,
                pt: 4,
                overflowY: 'auto',
                flex: 1,
              }}
            >
              {/* HEADER */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: dialogMode === 'create' ? 'flex-end' : 'space-between',
                  gap: 1,
                  mb: 0.5,
                }}
              >
                {/* Título SOLO en view/edit */}
                {dialogMode !== 'create' && (
                  <Typography sx={{ fontWeight: 600 }}>{dialogTitle}</Typography>
                )}

                {/* Acciones siempre a la derecha */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {isViewMode && (
                    <IconButton
                      size="small"
                      onClick={handleStartEditingEvent}
                      aria-label={t('global.edit', 'Editar')}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                  {canDelete && (
                    <IconButton
                      size="small"
                      onClick={isEvent ? handleDeleteEvent : handleDeleteTask}
                      color="error"
                      aria-label={t('global.delete', 'Eliminar')}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                  {isViewMode && (
                    <IconButton
                      size="small"
                      onClick={handleDuplicateItem}
                      aria-label={t('global.duplicate', 'Duplicar')}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>

              {/* Titulo en linea  + Tabs debajo */}
              {dialogMode === 'create' && (
                <>
                  <TitleTextField disabled={false} titleField={titleField} variantMode="inline" />

                  {/* Solo mostrar tabs si el título está vacío (creación nueva, no duplicado) */}
                  {!hiddenTabs && (
                    <CreateTypeTabs
                      value={dialogType}
                      onChange={handleChangeCreateType}
                      labels={{
                        event: t('global.event', 'Evento'),
                        task: t('global.task', 'Tarea'),
                      }}
                    />
                  )}
                </>
              )}

              {/* CONTENIDO */}
              {dialogType === 'event' ? (
                <>
                  {dialogMode !== 'create' && (
                    <TitleTextField disabled={dialogMode === 'view'} titleField={titleField} />
                  )}

                  <DescriptionTextField
                    disabled={dialogMode === 'view'}
                    descriptionField={descriptionField}
                  />

                  <TaskCalendarSelect
                    disabled={dialogMode === 'view'}
                    calendars={[...myCalendars, ...otherCalendars]}
                    userId={myCalendars[0]?.userId}
                    value={eventCalendarId}
                    onChange={(id) => setEventCalendarId(id)}
                    loading={isLoadingCalendars}
                  />

                  {/* Box contenedor para fecha, hora, todo el día y recurrencia */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                      // p: 2,
                      borderRadius: 1,
                      padding: '15px',
                      bgcolor: (theme) =>
                        //@ts-ignore
                        theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.05)'
                          : 'rgba(0, 0, 0, 0.02)',
                    }}
                  >
                    {/* Fila del ícono + selectores de fecha/hora */}
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                      {/* <AccessTimeIcon
                      sx={{
                        mt: 0.5,
                        color: 'text.secondary',
                        fontSize: '1.25rem',
                      }}
                    /> */}
                      <Box sx={{ flex: 1 }}>
                        <DatePicker
                          disabled={dialogMode === 'view'}
                          selectedStartDate={date.state.selectedStartDate}
                          selectedEndDate={date.state.selectedEndDate}
                          handleSelectStart={date.action.handleSelect(
                            date.action.setSelectedStartDate,
                          )}
                          handleSelectEnd={date.action.handleSelect(date.action.setSelectedEndDate)}
                          isError={date.state.error}
                          hideLabel={true}
                          allDay={eventFields?.allDay ?? false}
                        />
                      </Box>
                    </Box>

                    {/* Fila de todo el día y recurrencia */}
                    {eventFields && (
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          alignItems: 'center',
                        }}
                      >
                        <AllDayCheckbox
                          checked={eventFields.allDay}
                          onChange={eventFields.setAllDay}
                          disabled={dialogMode === 'view'}
                        />
                        <Box sx={{ flex: 1 }}>
                          <RecurrenceSelect
                            disabled={dialogMode === 'view'}
                            value={eventFields.recurrenceRule}
                            onChange={eventFields.setRecurrenceRule}
                            hideLabel={true}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>

                  {eventFields && (
                    <>
                      <ColorPicker
                        disabled={dialogMode === 'view'}
                        colorField={eventFields.colorField}
                        onColorChange={dialogMode === 'create' ? handleColorChange : undefined}
                      />
                    </>
                  )}

                  {notifications && (
                    <ReminderNotifications
                      disabled={dialogMode === 'view'}
                      notificationsField={notifications as any}
                    />
                  )}

                  <LinkContactsSelect
                    linkContactsHandler={linkContact}
                    disabled={dialogMode === 'view'}
                  />

                  {eventFields && (
                    <LocationTextField
                      disabled={dialogMode === 'view'}
                      locationField={eventFields.locationField}
                    />
                  )}
                </>
              ) : (
                <>
                  {dialogMode !== 'create' && (
                    <TitleTextField disabled={dialogMode === 'view'} titleField={titleField} />
                  )}

                  <DescriptionTextField
                    disabled={dialogMode === 'view'}
                    descriptionField={descriptionField}
                  />

                  <TaskCalendarSelect
                    disabled={dialogMode === 'view'}
                    calendars={[...myCalendars, ...otherCalendars]}
                    userId={myCalendars[0]?.userId}
                    value={taskCalendarId}
                    onChange={(id) => setTaskCalendarId(id)}
                    loading={isLoadingCalendars}
                  />

                  {/* Box contenedor para fecha y hora con estilo consistente */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                      p: 2,
                      borderRadius: 1,
                      bgcolor: (theme) =>
                        //@ts-ignore
                        theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.05)'
                          : 'rgba(0, 0, 0, 0.02)',
                    }}
                  >
                    {/* Fila del ícono + selector de fecha/hora */}
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                      <AccessTimeIcon
                        sx={{
                          mt: 0.5,
                          color: 'text.secondary',
                          fontSize: '1.25rem',
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <TaskDatePicker
                          disabled={dialogMode === 'view'}
                          selectedDateTime={date.state.selectedStartDate}
                          handleSelect={(dt) => {
                            date.action.setSelectedStartDate(dt);
                            date.action.setSelectedEndDate(dt);
                            date.action.setError(false);
                          }}
                          isError={date.state.error}
                          hideLabel={true}
                        />
                      </Box>
                    </Box>
                  </Box>

                  <LinkContactsSelect
                    linkContactsHandler={linkContact}
                    disabled={dialogMode === 'view'}
                  />
                </>
              )}
            </Box>

            {/* Action Buttons en la parte inferior */}
            <Box
              sx={{
                borderTop: 1,
                borderColor: 'divider',
                p: 2,
              }}
            >
              <ActionButtons isFinalized={selectedEvent?.finalized} />
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Diálogos de confirmación */}
      {/* Modal de confirmación para eliminar eventos/tareas normales */}
      <ConfirmDeleteDialog
        open={confirmDeleteDialog.open}
        onClose={confirmDeleteDialog.handleClose}
        onConfirm={handleConfirmDelete}
        itemTitle={
          confirmDeleteDialog.itemType === 'event' ? selectedEvent?.title : selectedTask?.title
        }
        itemType={confirmDeleteDialog.itemType}
      />

      {/* Modal de confirmación para eliminar eventos recurrentes */}
      <DeleteRecurringEventDialog
        open={deleteRecurringDialog.open}
        onClose={deleteRecurringDialog.handleClose}
        onConfirm={handleConfirmDeleteRecurring}
        eventTitle={selectedEvent?.title}
      />

      {/* Modal de confirmación para editar eventos recurrentes */}
      <EditRecurringEventDialog
        open={editRecurringDialog.open}
        onClose={editRecurringDialog.handleClose}
        onConfirm={handleConfirmEditRecurring}
        eventTitle={selectedEvent?.title}
      />

      {/* Snackbar para notificaciones de guardado */}
      <Snackbar
        open={savingSnackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={savingSnackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {savingSnackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EventDialog;
